import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { getUserPreferences } from "src/common/util/getUserPreferences";
import { isPlainObject } from "src/common/util/applyMergePatch";
import { readLocalStorage, removeLocalStorage } from "src/common/util/localStorage";
import { getTour, isAwaitStep } from "src/features/onboarding/registry";
import {
  selectActiveTour,
  selectOnboardingAudience,
} from "src/features/onboarding/state/onboardingSlice";
import {
  enqueuePreferencesPatch,
  refreshPreferences,
} from "src/features/profile/state/preferencesWriter";
import { getCurrentUser } from "src/features/profile/state/profileSlice";
import type { IUser } from "src/model/model";
import type { SidebarItemDef, SidebarSectionId } from "./sidebarConfig";

const LEGACY_COLLAPSE_STORAGE_KEY = "wixar.sidebar.collapsed";
const FLUSH_DELAY_MS = 500;

type CollapseState = Partial<Record<SidebarSectionId, boolean>>;

let preferencesLoadStarted = false;

const readCollapseState = (user?: IUser): CollapseState => {
  const stored = getUserPreferences({ currentUser: user })?.sidebarCollapsed;
  return isPlainObject(stored) ? stored : {};
};

const readLegacyCollapseState = (): CollapseState | null => {
  const raw = readLocalStorage(LEGACY_COLLAPSE_STORAGE_KEY);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isPlainObject(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const isStoredCollapsed = (state: CollapseState, id: SidebarSectionId) =>
  state[id] ?? (id === "parameters" || id === "favorites");

export const useSidebarCollapse = (
  visibleItems: SidebarItemDef[],
  dragForcedSection?: SidebarSectionId,
) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(getCurrentUser);
  const activeTour = useAppSelector(selectActiveTour);
  const audience = useAppSelector(selectOnboardingAudience);
  const [unsaved, setUnsaved] = useState<CollapseState>({});
  const unsavedRef = useRef<CollapseState>({});
  const flushTimerRef = useRef<number>();

  const collapseState = { ...readCollapseState(currentUser), ...unsaved };

  const flush = useCallback(() => {
    window.clearTimeout(flushTimerRef.current);
    flushTimerRef.current = undefined;
    const changes = unsavedRef.current;
    if (Object.keys(changes).length === 0) return;
    unsavedRef.current = {};
    void enqueuePreferencesPatch(dispatch, { sidebarCollapsed: changes });
    setUnsaved({});
  }, [dispatch]);

  useEffect(() => flush, [flush]);

  useEffect(() => {
    if (preferencesLoadStarted) return;
    preferencesLoadStarted = true;
    refreshPreferences(dispatch)
      .then((preferences) => {
        const legacy = readLegacyCollapseState();
        if (legacy === null) return;
        if (preferences.sidebarCollapsed === undefined && Object.keys(legacy).length > 0) {
          void enqueuePreferencesPatch(dispatch, { sidebarCollapsed: legacy });
        }
        removeLocalStorage(LEGACY_COLLAPSE_STORAGE_KEY);
      })
      .catch((error: unknown) => {
        preferencesLoadStarted = false;
        console.warn("[useSidebarCollapse] failed to load preferences", error);
      });
  }, [dispatch]);

  const store = (id: SidebarSectionId, collapsed: boolean) => {
    unsavedRef.current = { ...unsavedRef.current, [id]: collapsed };
    setUnsaved(unsavedRef.current);
    window.clearTimeout(flushTimerRef.current);
    flushTimerRef.current = window.setTimeout(flush, FLUSH_DELAY_MS);
  };

  const currentStep = activeTour
    ? getTour(activeTour.id, audience)?.steps[activeTour.stepIndex]
    : undefined;
  const anchor = currentStep && !isAwaitStep(currentStep) ? currentStep.anchor : null;
  const forcedSection = anchor
    ? visibleItems.find((item) => item.dataTour === anchor)?.section
    : undefined;

  const isCollapsed = (id: SidebarSectionId) =>
    id !== forcedSection && id !== dragForcedSection && isStoredCollapsed(collapseState, id);

  const toggle = (id: SidebarSectionId) => store(id, !isStoredCollapsed(collapseState, id));

  const expand = (id: SidebarSectionId) => {
    if (isStoredCollapsed(collapseState, id)) store(id, false);
  };

  return { isCollapsed, toggle, expand };
};
